import axios from "axios";

export const API_BASE = process.env.REACT_APP_AI_API_URL || "http://127.0.0.1:8000";

const ACCESS_TOKEN_KEY = "pulse_access_token";
const REFRESH_TOKEN_KEY = "pulse_refresh_token";

export type AuthUser = {
  id: string;
  email: string;
  auth_provider: string;
  created_at: string;
  // Not yet on /api/auth/me's response as of this writing (PB-013) - optional
  // until the backend lands it, so a missing field just reads as "off".
  two_factor_enabled?: boolean;
};

export type TokenResponse = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
};

// POST /api/auth/login returns this instead of a TokenResponse when the
// account has 2FA on - no session yet, challenge_token is only good for the
// matching /api/auth/2fa/login-verify call.
export type TwoFactorChallenge = {
  two_factor_required: true;
  challenge_token: string;
};

export const isTwoFactorChallenge = (value: TokenResponse | TwoFactorChallenge): value is TwoFactorChallenge =>
  (value as TwoFactorChallenge)?.two_factor_required === true;

export const getAccessToken = (): string | null => window.localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = (): string | null => window.localStorage.getItem(REFRESH_TOKEN_KEY);

export const setTokens = (tokens: TokenResponse) => {
  window.localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
  window.localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
};

export const clearTokens = () => {
  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(REFRESH_TOKEN_KEY);
};

export const isAuthenticated = (): boolean => Boolean(getAccessToken());

// Plain axios instance for the auth endpoints themselves (no interceptors -
// register/login/refresh must never accidentally attach a stale bearer token
// or get caught in the 401-refresh loop below).
const authOnlyApi = axios.create();

export const register = async (email: string, password: string): Promise<TokenResponse> => {
  const { data } = await authOnlyApi.post<TokenResponse>(`${API_BASE}/api/auth/register`, { email, password });
  setTokens(data);
  return data;
};

export const login = async (email: string, password: string): Promise<TokenResponse | TwoFactorChallenge> => {
  const { data } = await authOnlyApi.post<TokenResponse | TwoFactorChallenge>(`${API_BASE}/api/auth/login`, { email, password });
  if (isTwoFactorChallenge(data)) return data;
  setTokens(data);
  return data;
};

// POST /api/auth/2fa/login-verify {challenge_token, code} -> a normal
// TokenResponse, exactly like completing a password-only login. code is
// either a 6-digit TOTP or a recovery code - the backend decides which.
export const verifyLogin2fa = async (challengeToken: string, code: string): Promise<TokenResponse> => {
  const { data } = await authOnlyApi.post<TokenResponse>(`${API_BASE}/api/auth/2fa/login-verify`, {
    challenge_token: challengeToken,
    code,
  });
  setTokens(data);
  return data;
};

export const loginWithGoogle = async (idToken: string): Promise<TokenResponse> => {
  const { data } = await authOnlyApi.post<TokenResponse>(`${API_BASE}/api/auth/google`, { id_token: idToken });
  setTokens(data);
  return data;
};

// Exchanges a short-lived, single-use magic-login token (minted server-side
// by an admin, e.g. for "view as user") for a real session. The token itself
// is opaque here - it is never inspected or trusted client-side, only handed
// to the server, which does all validation (expiry, single-use, audit log).
export const exchangeMagicLoginToken = async (token: string): Promise<TokenResponse> => {
  const { data } = await authOnlyApi.post<TokenResponse>(`${API_BASE}/api/auth/magic-login/exchange`, { token });
  setTokens(data);
  return data;
};

export const refreshTokens = async (): Promise<TokenResponse> => {
  const refresh_token = getRefreshToken();
  if (!refresh_token) throw new Error("No refresh token available.");
  const { data } = await authOnlyApi.post<TokenResponse>(`${API_BASE}/api/auth/refresh`, { refresh_token });
  setTokens(data);
  return data;
};

export const logout = async (): Promise<void> => {
  const refresh_token = getRefreshToken();
  try {
    if (refresh_token) {
      await authOnlyApi.post(`${API_BASE}/api/auth/logout`, { refresh_token });
    }
  } finally {
    clearTokens();
  }
};

export const fetchCurrentUser = async (): Promise<AuthUser> => {
  const { data } = await pulseApi.get<AuthUser>(`${API_BASE}/api/auth/me`);
  return data;
};

// Shared axios instance for every authenticated Pulse product call.
// Attaches the access token, and on a 401 transparently refreshes once and
// retries the original request before giving up.
export const pulseApi = axios.create();

pulseApi.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

let refreshInFlight: Promise<TokenResponse> | null = null;

pulseApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    if (error?.response?.status !== 401 || !original || original._retried) {
      throw error;
    }
    original._retried = true;
    try {
      if (!refreshInFlight) {
        refreshInFlight = refreshTokens().finally(() => {
          refreshInFlight = null;
        });
      }
      const tokens = await refreshInFlight;
      original.headers = original.headers || {};
      original.headers["Authorization"] = `Bearer ${tokens.access_token}`;
      return pulseApi(original);
    } catch (refreshError) {
      clearTokens();
      window.dispatchEvent(new Event("pulse-auth-expired"));
      throw refreshError;
    }
  },
);
